/**
 * Support Reports API
 * Handles interactions with the support reports backend via Firebase Hosting rewrites
 */

import { authenticatedFetch } from './apiClient';
import { API_ROUTES } from '../shared/apiRoutes/apiRoutes';

// Endpoints
// - Admin list is standardized to v2 canonical route
const ADMIN_REPORTS_LIST = API_ROUTES.REPORTS_LIST;
// - v2 user-facing support routes
const REPORTS_V2_BASE = '/api/v2/reports';
// - v2 admin update route base
const REPORTS_ADMIN_V2_BASE = '/api/v2/admin/reports';

/**
 * Create a new support report
 * @param {Object} reportData - Report data to submit
 * @returns {Promise<Object>} - Created report
 */
export const createSupportReport = async reportData => {
  try {
    // Get the current user's UID from the authenticatedFetch function
    // The userId will be automatically included from the Firebase Auth token

    // Set default status if not provided
    const reportWithDefaults = {
      ...reportData,
      status: reportData.status || 'open',
    };

    // Use the relative path directly with authenticatedFetch
    const response = await authenticatedFetch(REPORTS_V2_BASE, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(reportWithDefaults),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create support report');
    }

    const json = await response.json();
    // Unwrap standardized envelope { success, data }
    return json && (Object.prototype.hasOwnProperty.call(json, 'data') ? json.data : json);
  } catch (error) {
    console.error('Error creating support report:', error);
    throw error;
  }
};

/**
 * Get all support reports (admin/tenant_admin will get tenant-specific reports)
 * @param {string} [status] - Optional status filter
 * @returns {Promise<Array>} - List of support reports
 */
/**
 * Get support queue reports (tenant or Signal Bridge, based on role).
 * @param {string|Object} [filters] - Status string or { status, type, view, tenantId, limit }
 * @returns {Promise<Array>}
 */
function reportListParams(filters, cursor) {
  const params = new URLSearchParams();
  if (typeof filters === 'string' && filters) {
    params.set('status', filters);
  } else if (filters && typeof filters === 'object') {
    if (filters.status) params.set('status', filters.status);
    if (filters.type) params.set('type', filters.type);
    if (filters.view) params.set('view', filters.view);
    if (filters.limit) params.set('limit', String(filters.limit));
  }
  if (cursor) params.set('cursor', cursor);
  return params;
}

export const getSupportReports = async (filters = null) => {
  const items = [];
  let cursor = '';
  try {
    for (let page = 0; page < 20; page += 1) {
      const params = reportListParams(filters, cursor);
      const query = params.toString();
      const endpoint = query ? `${ADMIN_REPORTS_LIST}?${query}` : ADMIN_REPORTS_LIST;
      const response = await authenticatedFetch(endpoint);
      if (!response.ok) {
        let message = 'Failed to fetch support reports';
        try {
          const error = await response.json();
          message = error.error || message;
        } catch (_parseError) {
          message = 'Failed to fetch support reports';
        }
        throw new Error(message);
      }
      const json = await response.json();
      const pageItems = Array.isArray(json) ? json : (json && Array.isArray(json.data) ? json.data : []);
      items.push(...pageItems);
      cursor = json && typeof json.nextCursor === 'string' ? json.nextCursor : '';
      if (!cursor) break;
    }
    return items;
  } catch (error) {
    console.error('Error fetching support reports:', error);
    throw error;
  }
};

/**
 * Get a single support report by ID
 * @param {string} reportId - Report ID
 * @returns {Promise<Object>} - Support report
 */
export const getSupportReport = async reportId => {
  try {
    // Use the relative path directly with authenticatedFetch
    const response = await authenticatedFetch(`${REPORTS_V2_BASE}/${reportId}`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch support report');
    }

    const json = await response.json();
    // Unwrap standardized envelope { success, data }
    return json && (Object.prototype.hasOwnProperty.call(json, 'data') ? json.data : json);
  } catch (error) {
    console.error(`Error fetching support report ${reportId}:`, error);
    throw error;
  }
};

/**
 * Update a support report (admin/tenant_admin only)
 * @param {string} reportId - Report ID
 * @param {Object} updateData - Data to update (status, isReleaseNote, releaseId)
 * @returns {Promise<Object>} - Updated report
 */
export const updateSupportReport = async (reportId, updateData) => {
  try {
    // Use the v2 admin update route
    const response = await authenticatedFetch(`${REPORTS_ADMIN_V2_BASE}/${reportId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updateData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update support report');
    }

    const json = await response.json();
    // Unwrap standardized envelope { success, data }
    return json && (Object.prototype.hasOwnProperty.call(json, 'data') ? json.data : json);
  } catch (error) {
    console.error(`Error updating support report ${reportId}:`, error);
    throw error;
  }
};

/**
 * Get support reports for the current authenticated user
 * @param {string} [status] - Optional status filter
 * @returns {Promise<Array>} - List of user's support reports
 */
export const getUserSupportReports = async (status = null, type = null) => {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (type) params.set('type', type);
  const query = params.toString();
  const base = `${REPORTS_V2_BASE}/user`;
  const endpoint = query ? `${base}?${query}` : base;

  try {
    const response = await authenticatedFetch(endpoint);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch your support reports');
    }

    const json = await response.json();
    // Unwrap standardized envelope { success, data } and ensure array return
    if (Array.isArray(json)) return json;
    if (json && Array.isArray(json.data)) return json.data;
    return [];
  } catch (error) {
    console.error('Error fetching user support reports:', error);
    throw error;
  }
};

/**
 * Get thread messages for a support report
 * @param {string} reportId - Report ID
 * @returns {Promise<Array>} - List of thread messages
 */
export const getSupportReportThread = async reportId => {
  try {
    console.log(`[SUPPORT-API] Fetching thread for report: ${reportId}`);
    const response = await authenticatedFetch(`${REPORTS_V2_BASE}/${reportId}/thread`, {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      console.error(`[SUPPORT-API] Server returned ${response.status} for thread fetch`);
      // Try to parse error response, but handle parsing failures
      try {
        const error = await response.json();
        throw new Error(error.error || `Server returned ${response.status}`);
      } catch (parseError) {
        throw new Error(`Failed to fetch thread messages: ${response.statusText}`);
      }
    }

    try {
      const json = await response.json();
      const data = Array.isArray(json) ? json : (json && Array.isArray(json.data) ? json.data : []);
      console.log(
        `[SUPPORT-API] Successfully fetched thread with ${Array.isArray(data) ? data.length : 0} messages`
      );
      return data;
    } catch (parseError) {
      console.error('[SUPPORT-API] Error parsing thread response:', parseError);
      // Return empty array instead of throwing to prevent UI from breaking
      return [];
    }
  } catch (error) {
    console.error(`[SUPPORT-API] Error fetching thread for report ${reportId}:`, error);
    throw error;
  }
};

/**
 * Post a new message to a support report thread
 * @param {string} reportId - Report ID
 * @param {Object} messageData - The message data to post
 * @param {string} messageData.message - The message content
 * @returns {Promise<Object>} - Created message
 */
export const postSupportReportThreadMessage = async (reportId, messageData) => {
  try {
    console.log(`[SUPPORT-API] Posting message to report: ${reportId}`, messageData);

    // Ensure we have a message property
    const payload = typeof messageData === 'string' ? { message: messageData } : messageData;

    const response = await authenticatedFetch(`${REPORTS_V2_BASE}/${reportId}/thread`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      console.error(`[SUPPORT-API] Server returned ${response.status} for message post`);
      // Try to parse error response, but handle parsing failures
      try {
        const error = await response.json();
        throw new Error(error.error || `Server returned ${response.status}`);
      } catch (parseError) {
        throw new Error(`Failed to post message: ${response.statusText}`);
      }
    }

    try {
      const json = await response.json();
      const data = json && (Object.prototype.hasOwnProperty.call(json, 'data') ? json.data : json);
      console.log('[SUPPORT-API] Successfully posted message:', data);
      return data;
    } catch (parseError) {
      console.error('[SUPPORT-API] Error parsing post response:', parseError);
      // Return a mock response to prevent UI from breaking
      return {
        id: `temp-${Date.now()}`,
        message: payload.message,
        createdAt: new Date().toISOString(),
        senderId: 'current-user',
        senderRole: 'user',
      };
    }
  } catch (error) {
    console.error(`[SUPPORT-API] Error posting message to thread for report ${reportId}:`, error);
    throw error;
  }
};
